import React, { useRef } from 'react';

import Link from '../../components/common/Link/Link';
import LandingPageHeader from '../../components/LandingPageHeader/LandingPageHeaderContainer';
import TextWithImage from '../../components/TextWithImage/TextWithImage';
import ExplainerSection from '../../components/ExplainerSection/ExplainerSection';
import ComparisonSection from '../../components/ComparisonSection/ComparisonSection';

import MaximizeYields from './MaximizeYields.svg';
import StakeAndVesting from './StakeAndVesting.svg';
import JoinCommunity from './JoinCommunity.svg';
import NotOnlyPolygon from './NotOnlyPolygon.svg';

import styles from './LandingPage.module.scss';

interface LandingPageProps {
  pools: any;
  stakingRewardsData: any;
  isVaultsLoaded: boolean;
}

const LandingPage: React.VFC<LandingPageProps> = ({
  pools,
  stakingRewardsData,
  isVaultsLoaded,
}) => {
  const maximizeYieldsScrollRef = useRef<HTMLDivElement>(null);

  const handleLearnMoreClick = () => {
    if (maximizeYieldsScrollRef && maximizeYieldsScrollRef.current) {
      maximizeYieldsScrollRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className={styles.container}>
      <LandingPageHeader
        pools={pools}
        stakingRewardsData={stakingRewardsData}
        isVaultsLoaded={isVaultsLoaded}
        onLearnMoreClick={handleLearnMoreClick}
      />

      <TextWithImage
        image={MaximizeYields}
        title="Maximize Yields with Adamant"
        text={
          <>
            <p>
              Adamant currently has over 150 vaults for Quickswap, Sushiswap, and other leading
              platforms on the Matic/Polygon network. Users deposit LP (Liquidity Provider) tokens
              to Adamant&apos;s vault contracts, which are then automatically compounded from 50 to
              1000+ times each day into more LP tokens.
            </p>
            <p>
              The power of exponential compound interest allows Adamant&apos;s vaults to earn much
              higher yields compared to normal staking. Users also avoid gas fees and the hassle of
              having to harvest and compound multiple pools by themselves.
            </p>
          </>
        }
        linkRight={
          <Link href="/vaults" color="primary">
            Go to Vaults
          </Link>
        }
        scrollRef={maximizeYieldsScrollRef}
      />

      <ExplainerSection />

      <TextWithImage
        image={StakeAndVesting}
        title="Stake and Vesting"
        text={
          <>
            <p>
              Stake ADDY to earn performance fee dividends from Adamant&apos;s vaults. Stakers can
              gain exposure to the returns from the entire Polygon DeFi scene without having to
              manage multiple platforms and farms.
            </p>
            <p>
              Freshly minted ADDY is vesting for 90 days. It is possible to withdraw before the 90
              days have passed, but doing so will incur a 50% early vesting withdrawal penalty. Your
              vesting ADDY will still earn performance fees during that time.
            </p>
          </>
        }
        linkRight={
          <Link href="/stakeaddy" color="primary">
            Go to Stake
          </Link>
        }
        reversed
      />

      <ComparisonSection />

      <TextWithImage
        image={JoinCommunity}
        title="Join our community"
        text={
          <p>
            If you need more information about Adamant, you can join our Discord community or read
            the latest news on our Twitter. On Discord you will find members of our team and meet
            many users who share their experience and knowledge. By participating in the Discord,
            you can also influence the future changes of Adamant.
          </p>
        }
        linkLeft={
          <Link href="https://t.me/adamantfinance" newTab color="primary">
            Visit Telegram
          </Link>
        }
        linkRight={
          <Link href="https://discord.gg/69ureKKYz2" newTab color="primary">
            Open Discord
          </Link>
        }
      />

      <TextWithImage
        image={NotOnlyPolygon}
        title="Not only on Polygon"
        text={
          <>
            <p>
              We are also present on Cronos, where it&apos;s possible for you to earn CADDYs. This
              network was built on Ethermint, which supports rapid porting of apps & smart contracts
              from Ethereum and other EVM-compatible chains.
            </p>
            <p>
              You can use Vaults, Staking and Boosting when you are connected to Cronos network.
            </p>
          </>
        }
        reversed
      />
    </div>
  );
};

export default LandingPage;
